import { authClient } from "@/lib/auth-client";
import { Toast, ErrorMessages } from "@/lib/utils";

export interface APIResponse<T> {
	success: boolean;
	message: string;
	data?: T;
	code?: string;
	status?: number;
}

/**
 * Authenticated fetch wrapper that includes auth cookies automatically
 * and handles common authentication errors with automatic retry
 */
export class AuthenticatedAPIClient {
	private static baseURL = "/v1";
	private static isRetrying = false;

	/**
	 * Make an authenticated API request with automatic retry on 401 errors
	 */
	private static async fetchWithAuth(
		endpoint: string,
		options: RequestInit = {},
		isRetry = false,
	): Promise<Response> {
		const url = `${this.baseURL}${endpoint}`;

		const response = await fetch(url, {
			...options,
			headers: {
				"Content-Type": "application/json",
				...options.headers,
			},
			credentials: "include", // Include auth cookies
		});

		// Handle 401 errors by attempting to refresh session and retry
		if (response.status === 401 && !isRetry && !this.isRetrying) {
			console.warn("Received 401 error, attempting to refresh session...");

			try {
				this.isRetrying = true;

				// Try to refresh the session
				const sessionResult = await authClient.getSession();

				if (sessionResult.data?.user) {
					console.log("Session refreshed successfully, retrying request...");
					// Retry the original request with the refreshed session
					return this.fetchWithAuth(endpoint, options, true);
				} else {
					// Session refresh failed, redirect to login
					console.error("Session refresh failed, redirecting to login");
					Toast.error(ErrorMessages.AUTH_REQUIRED);
					this.handleAuthError();
					throw new Error("Authentication required. Please log in again.");
				}
			} catch (refreshError) {
				console.error("Session refresh error:", refreshError);
				Toast.error(ErrorMessages.AUTH_REQUIRED);
				this.handleAuthError();
				throw new Error("Authentication required. Please log in again.");
			} finally {
				this.isRetrying = false;
			}
		}

		// If still 401 after retry, or if it's already a retry attempt
		if (response.status === 401) {
			console.error("Authentication failed after retry attempt");
			Toast.error(ErrorMessages.AUTH_REQUIRED);
			this.handleAuthError();
			throw new Error("Authentication required. Please log in again.");
		}

		return response;
	}

	/**
	 * Handle authentication errors globally
	 */
	private static handleAuthError(): void {
		// Clear any cached auth data
		try {
			// Force redirect to login page
			window.location.href = "/auth/login";
		} catch (error) {
			console.error("Error handling auth error:", error);
		}
	}

	/**
	 * Get user-friendly error message based on HTTP status code
	 */
	private static getErrorMessage(status: number): string {
		switch (status) {
			case 400:
				return ErrorMessages.VALIDATION_ERROR;
			case 403:
				return ErrorMessages.PERMISSION_DENIED;
			case 404:
				return ErrorMessages.NOT_FOUND;
			// Without this a duplicate code fell through to UNKNOWN_ERROR, which is
			// actively misleading — the request was understood and refused, not
			// mysterious. Only reached when the body carried no message of its own.
			case 409:
				return "Dữ liệu đã tồn tại";
			case 500:
			case 502:
			case 503:
			case 504:
				return ErrorMessages.SERVER_ERROR;
			default:
				return ErrorMessages.UNKNOWN_ERROR;
		}
	}

	/**
	 * Read the API's own error message out of a failed response.
	 *
	 * Every `/v1` endpoint answers with `{ success, message, ... }`, and for 4xx
	 * that message is readable Vietnamese written for the person on screen —
	 * "Mã khoa ... đã tồn tại" rather than "409". Discarding it, as this client
	 * used to, meant a duplicate code surfaced as a status number.
	 *
	 * Returns `null` rather than throwing when the body is absent, not JSON, or
	 * carries no usable message: an nginx 502 is HTML, and a dropped connection
	 * is nothing at all. Those cases must still produce the old uniform error.
	 */
	private static async readErrorMessage(response: Response): Promise<string | null> {
		try {
			const body = (await response.json()) as { message?: unknown } | null;
			const message = body?.message;
			return typeof message === "string" && message.trim().length > 0 ? message : null;
		} catch {
			return null;
		}
	}

	/**
	 * The single place a non-ok response becomes the error every caller sees.
	 *
	 * Falls back to the previous status-derived wording whenever the body has no
	 * message, so callers that never sent one behave exactly as before.
	 *
	 * `silentErrors` bỏ TOAST, không bỏ lỗi: hàm vẫn trả về đúng `Error` đó và nơi
	 * gọi vẫn phải xử lý. Dành cho màn hình đã có chỗ hiển thị lỗi của riêng nó —
	 * tra cứu MSSV có banner đỏ ngay dưới ô nhập, nên thêm toast là nói hai lần
	 * cùng một điều. Mặc định vẫn toast, vì phần lớn nơi gọi không có chỗ nào khác
	 * để báo lỗi.
	 */
	private static async toError(response: Response, silentErrors = false): Promise<Error> {
		const apiMessage = await this.readErrorMessage(response);

		if (!silentErrors) {
			Toast.error(apiMessage ?? this.getErrorMessage(response.status));
		}

		// `status` đi kèm lỗi để nơi gọi phân loại được mà không phải đọc câu chữ.
		// Trước đây `student-search.tsx` nhận ra "không tìm thấy" bằng regex trên
		// nguyên văn message — thứ vừa đổi sang tiếng Việt, và sẽ còn đổi nữa. Một
		// câu chữ là bản dịch; một mã status là hợp đồng.
		return Object.assign(new Error(apiMessage ?? `HTTP error! status: ${response.status}`), {
			status: response.status,
		});
	}

	/**
	 * GET request
	 */
	static async get<T>(
		endpoint: string,
		options: { silentErrors?: boolean } = {},
	): Promise<APIResponse<T>> {
		const response = await this.fetchWithAuth(endpoint, {
			method: "GET",
		});

		if (!response.ok) {
			throw await this.toError(response, options.silentErrors);
		}

		return response.json();
	}

	/**
	 * POST request
	 */
	static async post<T, U = any>(endpoint: string, data?: U): Promise<APIResponse<T>> {
		const response = await this.fetchWithAuth(endpoint, {
			method: "POST",
			body: data ? JSON.stringify(data) : undefined,
		});

		if (!response.ok) {
			throw await this.toError(response);
		}

		return response.json();
	}

	/**
	 * PUT request
	 */
	static async put<T, U = any>(endpoint: string, data?: U): Promise<APIResponse<T>> {
		const response = await this.fetchWithAuth(endpoint, {
			method: "PUT",
			body: data ? JSON.stringify(data) : undefined,
		});

		if (!response.ok) {
			throw await this.toError(response);
		}

		return response.json();
	}

	/**
	 * PATCH request
	 */
	static async patch<T, U = any>(endpoint: string, data?: U): Promise<APIResponse<T>> {
		const response = await this.fetchWithAuth(endpoint, {
			method: "PATCH",
			body: data ? JSON.stringify(data) : undefined,
		});

		if (!response.ok) {
			throw await this.toError(response);
		}

		return response.json();
	}

	/**
	 * DELETE request
	 */
	static async delete<T>(endpoint: string): Promise<APIResponse<T>> {
		const response = await this.fetchWithAuth(endpoint, {
			method: "DELETE",
		});

		if (!response.ok) {
			throw await this.toError(response);
		}

		return response.json();
	}

	/**
	 * Check if user is authenticated
	 */
	static async checkAuth(): Promise<boolean> {
		try {
			const session = await authClient.getSession();
			return !!session.data?.user;
		} catch {
			return false;
		}
	}
}

/**
 * React hook for making authenticated API calls with loading and error states
 */
export function useAuthenticatedAPI() {
	const { data } = authClient.useSession();
	const isAuthenticated = !!data?.user;

	const makeRequest = async <T>(
		requestFn: () => Promise<APIResponse<T>>,
	): Promise<{ data: T | null; error: string | null; loading: boolean }> => {
		if (!isAuthenticated) {
			return {
				data: null,
				error: "Not authenticated",
				loading: false,
			};
		}

		try {
			const response = await requestFn();
			return {
				data: response.data || null,
				error: null,
				loading: false,
			};
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Unknown error";

			// Auth errors are already handled in fetchWithAuth
			return {
				data: null,
				error: errorMessage,
				loading: false,
			};
		}
	};

	return {
		makeRequest,
		isAuthenticated,
	};
}
